import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import SearchableSelect from '../components/SearchableSelect';
import {
  CubeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const AdminPackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [limits, setLimits] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_usd: '',
    price_local: '',
    local_currency: 'GHS',
    billing_cycle: 'monthly',
    is_recommended: false,
    cta_text: 'Get Started',
    display_order: 0
  });

  const [limitFormData, setLimitFormData] = useState({
    limit_type: '',
    limit_value: ''
  });

  // Default values for different limit types (based on scale package)
  const getDefaultLimitValue = (limitType) => {
    const defaults = {
      'max_document_size_bytes': '4194304', // 4MB (scale package default)
      'max_text_length': '4000',           // 4K chars (scale package default)
      'max_tokens_per_document': '1000',   // 1K tokens (scale package default)
      'max_chunk_size': '2000',
      'max_chunks_per_document': '40',     // 40 chunks (scale package default)
      'monthly_characters': '10000',
      'whatsapp_agents': '5',
      'ai_agents': '3',
      'users_per_org': '10',
      'knowledge_base_documents': '50',
      'monthly_messages': '1000',
      'api_access': 'true',
      'webhook_support': 'true',
      'custom_integrations': 'false',
      'white_labeling': 'false',
      'priority_support': 'false',
      'dedicated_manager': 'false'
    };
    return defaults[limitType] || '';
  };

  // Get current value for a limit type
  const getCurrentLimitValue = (limitType) => {
    const existingLimit = limits.find(limit => limit.limit_type === limitType);
    return existingLimit ? existingLimit.limit_value : null;
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await api.getPackages();
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageLimits = async (packageId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/packages/${packageId}/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setLimits(data.limits || []);
    } catch (error) {
      console.error('Failed to fetch package limits:', error);
      setLimits([]);
      toast.error('Failed to load package limits');
    }
  };

  const handleCreatePackage = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      toast.success('Package created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      console.error('Failed to create package:', error);
      toast.error('Failed to create package');
    }
  };

  const handleUpdatePackage = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/packages/${editingPackage.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      toast.success('Package updated successfully');
      setShowEditModal(false);
      setEditingPackage(null);
      resetForm();
      fetchPackages();
    } catch (error) {
      console.error('Failed to update package:', error);
      toast.error('Failed to update package');
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/packages/${packageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Failed to delete package:', error);
      toast.error('Failed to delete package');
    }
  };

  const handleLimitTypeChange = (limitType) => {
    const currentValue = getCurrentLimitValue(limitType);
    const defaultValue = getDefaultLimitValue(limitType);

    // If limit already exists, show current value; otherwise show default
    const suggestedValue = currentValue !== null ? currentValue : defaultValue;

    setLimitFormData(prev => ({
      ...prev,
      limit_type: limitType,
      limit_value: suggestedValue
    }));
  };

  const handleAddLimit = async () => {
    if (!limitFormData.limit_type || !limitFormData.limit_value) {
      toast.error('Please fill in all limit fields');
      return;
    }

    const currentValue = getCurrentLimitValue(limitFormData.limit_type);

    try {
      const token = localStorage.getItem('admin_token');
      const method = currentValue !== null ? 'PUT' : 'POST';
      const url = currentValue !== null
        ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/packages/limits/${limits.find(l => l.limit_type === limitFormData.limit_type)?.id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/admin/packages/${selectedPackage.id}/limits`;

      console.log('Making request:', { method, url, data: limitFormData });

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(limitFormData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response is ok
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          console.log('Error response data:', errorData);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          // Try to get text response
          try {
            const textResponse = await response.text();
            console.log('Raw error response:', textResponse);
          } catch (textError) {
            console.error('Failed to get text response:', textError);
          }
        }
        throw new Error(errorMessage);
      }

      // Parse successful response
      let result;
      try {
        result = await response.json();
        console.log('Success response data:', result);
      } catch (parseError) {
        console.error('Failed to parse success response:', parseError);
        throw new Error('Invalid response format from server');
      }

      if (result.success) {
        console.log('Showing success toast');
        toast.success(currentValue !== null ? 'Limit updated successfully' : 'Limit added successfully');
        setLimitFormData({ limit_type: '', limit_value: '' });
        fetchPackageLimits(selectedPackage.id);
      } else {
        console.log('Server returned success=false:', result);
        throw new Error(result.error || result.message || 'Failed to save limit');
      }
    } catch (error) {
      console.error('Failed to save limit:', error);
      toast.error(error.message || 'Failed to save limit');
    }
  };

  const handleDeleteLimit = async (limitId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/packages/limits/${limitId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      // Check if response is ok
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse successful response
      const result = await response.json();

      if (result.success) {
        toast.success('Limit deleted successfully');
        fetchPackageLimits(selectedPackage.id);
      } else {
        throw new Error(result.error || 'Failed to delete limit');
      }
    } catch (error) {
      console.error('Failed to delete limit:', error);
      toast.error(error.message || 'Failed to delete limit');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price_usd: '',
      price_local: '',
      local_currency: 'GHS',
      billing_cycle: 'monthly',
      is_recommended: false,
      cta_text: 'Get Started',
      display_order: 0
    });
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      price_usd: pkg.price_usd || '',
      price_local: pkg.price_local || '',
      local_currency: pkg.local_currency || 'GHS',
      billing_cycle: pkg.billing_cycle || 'monthly',
      is_recommended: pkg.is_recommended || false,
      cta_text: pkg.cta_text || 'Get Started',
      display_order: pkg.display_order || 0
    });
    setShowEditModal(true);
  };

  const openLimitsModal = (pkg) => {
    setSelectedPackage(pkg);
    fetchPackageLimits(pkg.id);
  };

  const getLimitTypeDisplayName = (limitType) => {
    const displayNames = {
      'max_document_size_bytes': 'Max Document Size',
      'max_text_length': 'Max Text Length',
      'max_tokens_per_document': 'Max Tokens per Document',
      'max_chunk_size': 'Max Chunk Size',
      'max_chunks_per_document': 'Max Chunks per Document',
      'monthly_characters': 'Monthly Characters',
      'whatsapp_agents': 'WhatsApp Agents',
      'ai_agents': 'AI Agents',
      'users_per_org': 'Users per Organization',
      'knowledge_base_documents': 'Knowledge Base Documents',
      'monthly_messages': 'Monthly Messages',
      'api_access': 'API Access',
      'webhook_support': 'Webhook Support',
      'custom_integrations': 'Custom Integrations',
      'white_labeling': 'White Labeling',
      'priority_support': 'Priority Support',
      'dedicated_manager': 'Dedicated Manager'
    };
    return displayNames[limitType] || limitType;
  };

  const formatLimitValue = (limitType, value) => {
    if (value === 'unlimited') return 'Unlimited';

    // Format byte values
    if (limitType === 'max_document_size_bytes') {
      const mb = parseInt(value) / (1024 * 1024);
      return `${mb}MB (${value} bytes)`;
    }

    // Format boolean values
    if (['api_access', 'webhook_support', 'custom_integrations', 'white_labeling', 'priority_support', 'dedicated_manager'].includes(limitType)) {
      return value === 'true' ? 'Enabled' : 'Disabled';
    }

    // Format numeric values with commas
    if (!isNaN(value)) {
      return parseInt(value).toLocaleString();
    }

    return value;
  };

  const getLimitDescription = (limitType) => {
    const descriptions = {
      'max_document_size_bytes': 'Maximum size allowed for uploaded documents',
      'max_text_length': 'Maximum characters allowed in document text',
      'max_tokens_per_document': 'Maximum AI tokens per document for processing',
      'max_chunk_size': 'Maximum characters per text chunk',
      'max_chunks_per_document': 'Maximum number of chunks per document',
      'monthly_characters': 'Total characters allowed per month',
      'whatsapp_agents': 'Number of WhatsApp agents allowed',
      'ai_agents': 'Number of AI agents allowed',
      'users_per_org': 'Maximum users per organization',
      'knowledge_base_documents': 'Maximum documents in knowledge base',
      'monthly_messages': 'Maximum messages per month',
      'api_access': 'Whether API access is enabled',
      'webhook_support': 'Whether webhook support is enabled',
      'custom_integrations': 'Whether custom integrations are allowed',
      'white_labeling': 'Whether white labeling is enabled',
      'priority_support': 'Whether priority support is enabled',
      'dedicated_manager': 'Whether dedicated account manager is provided'
    };
    return descriptions[limitType] || '';
  };

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Package Management</h1>
                <p className="text-gray-600">Manage subscription packages and their limits</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-whatsapp-600 hover:bg-whatsapp-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Package
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Packages Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading packages...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div key={pkg.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <CubeIcon className="h-8 w-8 text-whatsapp-600 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{pkg.name}</h3>
                        {pkg.is_recommended && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-whatsapp-100 text-whatsapp-800">
                            Recommended
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openLimitsModal(pkg)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Manage Limits"
                      >
                        <ExclamationTriangleIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(pkg)}
                        className="text-gray-600 hover:text-gray-900 p-1"
                        title="Edit Package"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePackage(pkg.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete Package"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-3">{pkg.description}</p>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Price:</span>
                        <span className="text-lg font-semibold text-gray-900">
                          ${pkg.price_usd}/{pkg.billing_cycle}
                        </span>
                      </div>
                      {pkg.price_local && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Local Price:</span>
                          <span className="text-sm text-gray-900">
                            {pkg.price_local} {pkg.local_currency}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Display Order:</span>
                        <span className="text-sm text-gray-900">{pkg.display_order}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Package Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Package</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="e.g., Pro Plan"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">USD Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_usd}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_usd: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="10.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Local Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_local}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_local: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="50.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Local Currency</label>
                    <input
                      type="text"
                      value={formData.local_currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, local_currency: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="GHS"
                    />
                  </div>

                  <div>
                    <SearchableSelect
                      label="Billing Cycle"
                      name="billingCycle"
                      options={[
                        { value: 'monthly', label: 'Monthly', icon: <CalendarDaysIcon className="h-4 w-4 text-blue-600" /> },
                        { value: 'yearly', label: 'Yearly', icon: <ClockIcon className="h-4 w-4 text-green-600" /> }
                      ]}
                      value={formData.billing_cycle}
                      onChange={(e) => setFormData(prev => ({ ...prev, billing_cycle: e.target.value }))}
                      placeholder="Select Billing Cycle..."
                      isSearchable={false}
                      isRequired={true}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text</label>
                    <input
                      type="text"
                      value={formData.cta_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="Get Started"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      id="is_recommended"
                      type="checkbox"
                      checked={formData.is_recommended}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_recommended: e.target.checked }))}
                      className="h-4 w-4 text-whatsapp-600 focus:ring-whatsapp-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_recommended" className="ml-2 block text-sm font-medium text-gray-700">
                      Mark as Recommended
                    </label>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="Package description..."
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePackage}
                    className="px-4 py-2 text-sm font-medium text-white bg-whatsapp-600 border border-transparent rounded-md hover:bg-whatsapp-700"
                  >
                    Create Package
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Package Modal */}
        {showEditModal && editingPackage && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Package: {editingPackage.name}</h3>

                {/* Same form fields as create modal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="e.g., Pro Plan"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">USD Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_usd}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_usd: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="10.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Local Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_local}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_local: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="50.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Local Currency</label>
                    <input
                      type="text"
                      value={formData.local_currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, local_currency: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="GHS"
                    />
                  </div>

                  <div>
                    <SearchableSelect
                      label="Billing Cycle"
                      name="billingCycle"
                      options={[
                        { value: 'monthly', label: 'Monthly', icon: <CalendarDaysIcon className="h-4 w-4 text-blue-600" /> },
                        { value: 'yearly', label: 'Yearly', icon: <ClockIcon className="h-4 w-4 text-green-600" /> }
                      ]}
                      value={formData.billing_cycle}
                      onChange={(e) => setFormData(prev => ({ ...prev, billing_cycle: e.target.value }))}
                      placeholder="Select Billing Cycle..."
                      isSearchable={false}
                      isRequired={true}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text</label>
                    <input
                      type="text"
                      value={formData.cta_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="Get Started"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      id="is_recommended_edit"
                      type="checkbox"
                      checked={formData.is_recommended}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_recommended: e.target.checked }))}
                      className="h-4 w-4 text-whatsapp-600 focus:ring-whatsapp-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_recommended_edit" className="ml-2 block text-sm font-medium text-gray-700">
                      Mark as Recommended
                    </label>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="Package description..."
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingPackage(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePackage}
                    className="px-4 py-2 text-sm font-medium text-white bg-whatsapp-600 border border-transparent rounded-md hover:bg-whatsapp-700"
                  >
                    Update Package
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Package Limits Modal */}
        {selectedPackage && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Manage Limits: {selectedPackage.name}
                  </h3>
                  <button
                    onClick={() => setSelectedPackage(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Add/Edit Limit */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                   <h4 className="text-sm font-medium text-gray-900 mb-3">
                     {limitFormData.limit_type && getCurrentLimitValue(limitFormData.limit_type) !== null ? 'Edit Limit' : 'Add New Limit'}
                   </h4>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                     <div>
                       <SearchableSelect
                         label="Limit Type"
                         name="limitType"
                         options={[
                           { value: '', label: 'Select limit type...', icon: <CubeIcon className="h-4 w-4 text-gray-400" /> },
                           { value: 'max_document_size_bytes', label: 'Max Document Size (bytes)', icon: <CubeIcon className="h-4 w-4 text-blue-600" /> },
                           { value: 'max_text_length', label: 'Max Text Length (chars)', icon: <CubeIcon className="h-4 w-4 text-green-600" /> },
                           { value: 'max_tokens_per_document', label: 'Max Tokens per Document', icon: <CubeIcon className="h-4 w-4 text-purple-600" /> },
                           { value: 'max_chunk_size', label: 'Max Chunk Size (chars)', icon: <CubeIcon className="h-4 w-4 text-orange-600" /> },
                           { value: 'max_chunks_per_document', label: 'Max Chunks per Document', icon: <CubeIcon className="h-4 w-4 text-red-600" /> },
                           { value: 'monthly_characters', label: 'Monthly Characters', icon: <CubeIcon className="h-4 w-4 text-indigo-600" /> },
                           { value: 'whatsapp_agents', label: 'WhatsApp Agents', icon: <CubeIcon className="h-4 w-4 text-green-500" /> },
                           { value: 'ai_agents', label: 'AI Agents', icon: <CubeIcon className="h-4 w-4 text-blue-500" /> },
                           { value: 'users_per_org', label: 'Users per Organization', icon: <CubeIcon className="h-4 w-4 text-purple-500" /> },
                           { value: 'knowledge_base_documents', label: 'Knowledge Base Documents', icon: <CubeIcon className="h-4 w-4 text-yellow-600" /> },
                           { value: 'monthly_messages', label: 'Monthly Messages', icon: <CubeIcon className="h-4 w-4 text-pink-600" /> },
                           { value: 'api_access', label: 'API Access', icon: <CubeIcon className="h-4 w-4 text-teal-600" /> },
                           { value: 'webhook_support', label: 'Webhook Support', icon: <CubeIcon className="h-4 w-4 text-cyan-600" /> },
                           { value: 'custom_integrations', label: 'Custom Integrations', icon: <CubeIcon className="h-4 w-4 text-lime-600" /> },
                           { value: 'white_labeling', label: 'White Labeling', icon: <CubeIcon className="h-4 w-4 text-amber-600" /> },
                           { value: 'priority_support', label: 'Priority Support', icon: <CubeIcon className="h-4 w-4 text-emerald-600" /> },
                           { value: 'dedicated_manager', label: 'Dedicated Manager', icon: <CubeIcon className="h-4 w-4 text-rose-600" /> }
                         ]}
                         value={limitFormData.limit_type}
                         onChange={(e) => handleLimitTypeChange(e.target.value)}
                         placeholder="Select limit type..."
                         isSearchable={true}
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         Limit Value
                         {limitFormData.limit_type && getCurrentLimitValue(limitFormData.limit_type) !== null && (
                           <span className="ml-2 text-xs text-blue-600">
                             (Current: {getCurrentLimitValue(limitFormData.limit_type)})
                           </span>
                         )}
                       </label>
                       <input
                         type="text"
                         value={limitFormData.limit_value}
                         onChange={(e) => setLimitFormData(prev => ({ ...prev, limit_value: e.target.value }))}
                         className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                         placeholder={limitFormData.limit_type ? `e.g., ${getDefaultLimitValue(limitFormData.limit_type)}` : "e.g., 1000, unlimited, true"}
                       />
                       {limitFormData.limit_type && (
                         <p className="mt-1 text-xs text-gray-500">
                           {getCurrentLimitValue(limitFormData.limit_type) !== null
                             ? "Leave unchanged to keep current value"
                             : `Suggested: ${getDefaultLimitValue(limitFormData.limit_type)}`
                           }
                         </p>
                       )}
                     </div>
                     <div className="flex items-end">
                       <button
                         onClick={handleAddLimit}
                         disabled={!limitFormData.limit_type || !limitFormData.limit_value}
                         className="w-full px-4 py-2 text-sm font-medium text-white bg-whatsapp-600 border border-transparent rounded-md hover:bg-whatsapp-700 disabled:opacity-50 disabled:cursor-not-allowed"
                       >
                         {limitFormData.limit_type && getCurrentLimitValue(limitFormData.limit_type) !== null ? 'Update Limit' : 'Add Limit'}
                       </button>
                     </div>
                   </div>
                 </div>

                {/* Current Limits */}
                <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-3">Current Limits ({limits.length})</h4>
                   {limits.length === 0 ? (
                     <div className="text-center py-8 text-gray-500">
                       <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                       <h3 className="mt-4 text-lg font-medium text-gray-900">No limits configured</h3>
                       <p className="mt-2 text-gray-600">Add limits above to configure this package.</p>
                     </div>
                   ) : (
                     <div className="space-y-3">
                       {limits.map((limit) => (
                         <div key={limit.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                           <div className="flex-1">
                             <div className="flex items-center justify-between">
                               <p className="text-sm font-medium text-gray-900">
                                 {getLimitTypeDisplayName(limit.limit_type)}
                               </p>
                               <div className="flex items-center space-x-2">
                                 <button
                                   onClick={() => handleLimitTypeChange(limit.limit_type)}
                                   className="text-blue-600 hover:text-blue-900 p-1"
                                   title="Edit this limit"
                                 >
                                   <PencilIcon className="h-4 w-4" />
                                 </button>
                                 <button
                                   onClick={() => handleDeleteLimit(limit.id)}
                                   className="text-red-600 hover:text-red-900 p-1"
                                   title="Delete Limit"
                                 >
                                   <TrashIcon className="h-4 w-4" />
                                 </button>
                               </div>
                             </div>
                             <p className="text-sm font-semibold text-green-600 mt-1">
                               {formatLimitValue(limit.limit_type, limit.limit_value)}
                             </p>
                             {getLimitDescription(limit.limit_type) && (
                               <p className="text-xs text-gray-500 mt-1">
                                 {getLimitDescription(limit.limit_type)}
                               </p>
                             )}
                           </div>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPackageManagement;
